import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ShieldAlert, UserPlus } from 'lucide-react';
import { addAccountFromStaging, addAllFromStaging, listAccountStaging, patchAccountStaging } from '@/api/accountStaging';
import { listAccountTypes } from '@/api/accountTypes';
import { ApiError } from '@/api/client';
import { RequireRole } from '@/auth/RequireRole';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDateTime } from '@/lib/dateFormat';
import { useSort, sortRows } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import type { AccountStagingItem } from '@/types/oscar';

type Tab = 'pending' | 'ignored' | 'added';

/**
 * Pending accounts — the Entra accounts the sync brought in that aren't yet a
 * Person / system account. One-click categorised "Add" promotes a row to the
 * right table (by the Account Type's Kind); disabled accounts land as
 * "NeedsVerify" and are flagged before adding. Admin / helpdesk only.
 */
export function PendingAccounts() {
  return (
    <section>
      <RequireRole anyOf={['oscar-admin', 'oscar-helpdesk']}>
        <PendingAccountsInner />
      </RequireRole>
    </section>
  );
}

function PendingAccountsInner() {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab>('pending');
  const [q, setQ] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const { sortKey, sortDir, onSort } = useSort('account');
  const [addTarget, setAddTarget] = useState<AccountStagingItem | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['account-staging'], queryFn: ({ signal }) => listAccountStaging(undefined, signal) });
  const items = useMemo(() => query.data ?? [], [query.data]);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['account-staging'] });

  // Eligible for the one-time bulk add: enabled (New) with a suggested type.
  const bulkEligible = useMemo(() => items.filter((a) => a.TriageStatus === 'New' && a.SuggestedAccountTypeCode).length, [items]);

  const bulkM = useMutation({
    mutationFn: () => addAllFromStaging(),
    onSuccess: (res) => {
      setBulkOpen(false);
      setNotice(`Added ${res.Added} account(s). ${res.SkippedNoType} skipped (no suggested type), ${res.SkippedNoEmail} skipped (no email), ${res.Failed} already existed.`);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['people'] });
    },
    onError: (e) => { setBulkOpen(false); setError(e instanceof ApiError ? e.message : 'Bulk add failed.'); },
  });

  const patchM = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'New' | 'Ignored' }) => patchAccountStaging(id, { TriageStatus: status }),
    onSuccess: invalidate,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not update the account.'),
  });

  const term = q.trim().toLowerCase();
  const filtered = useMemo(() => items.filter((a) => {
    const inTab =
      tab === 'pending' ? (a.TriageStatus === 'New' || a.TriageStatus === 'NeedsVerify')
        : tab === 'ignored' ? a.TriageStatus === 'Ignored'
          : a.TriageStatus === 'Added';
    if (!inTab) return false;
    if (enabledFilter === 'enabled' && !a.AccountEnabled) return false;
    if (enabledFilter === 'disabled' && a.AccountEnabled) return false;
    if (term && !`${a.DisplayName} ${a.Upn ?? ''} ${a.Mail ?? ''}`.toLowerCase().includes(term)) return false;
    return true;
  }), [items, tab, term, enabledFilter]);

  const sorted = useMemo(() => sortRows(filtered, (a) => {
    switch (sortKey) {
      case 'status': return a.AccountEnabled;
      case 'department': return a.Department;
      case 'lastSignIn': return a.LastSignInDateTime;
      case 'suggested': return a.SuggestedAccountTypeCode;
      default: return a.DisplayName;
    }
  }, sortDir), [filtered, sortKey, sortDir]);

  const counts = useMemo(() => ({
    pending: items.filter((a) => a.TriageStatus === 'New' || a.TriageStatus === 'NeedsVerify').length,
    ignored: items.filter((a) => a.TriageStatus === 'Ignored').length,
    added: items.filter((a) => a.TriageStatus === 'Added').length,
  }), [items]);

  const tabs: [Tab, string][] = [
    ['pending', `To add (${counts.pending})`],
    ['ignored', `Ignored (${counts.ignored})`],
    ['added', `Added (${counts.added})`],
  ];

  return (
    <>
      <header className="mb-5">
        <Link to="/people" className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-navy hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> People / Accounts
        </Link>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy">
          <UserPlus className="h-6 w-6 text-navy" aria-hidden /> Pending accounts
        </h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          Entra accounts brought in by the sync that aren&rsquo;t categorised yet. Add each one with an
          Account Type — it becomes a person or a system account accordingly. Disabled accounts are
          flagged to verify first.
        </p>
      </header>

      {error && <div role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{error}</div>}
      {notice && <div className="mb-3 rounded border border-green/40 bg-green/10 px-3 py-2 text-sm text-darkGrey">{notice}</div>}

      {query.isLoading && <p className="text-sm text-darkGrey/60">Loading…</p>}
      {query.isError && <p className="text-sm text-red">Could not load pending accounts.</p>}

      {!query.isLoading && !query.isError && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex rounded border border-border bg-white p-0.5 text-sm" role="tablist">
              {tabs.map(([k, label]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                  className={tab === k ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}>
                  {label}
                </button>
              ))}
            </div>
            {/* One-time bulk add (admin) — promotes all enabled accounts with a suggested type. */}
            {isAdmin && tab === 'pending' && bulkEligible > 0 && (
              <button type="button" onClick={() => { setError(null); setNotice(null); setBulkOpen(true); }}
                className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90">
                Add all ({bulkEligible})
              </button>
            )}
          </div>

          <div className="mb-3 flex flex-wrap items-end gap-3">
            <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey" style={{ minWidth: '12rem' }}>
              Search
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" aria-label="Search pending accounts"
                className="mt-1 w-full max-w-sm rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
            </label>
            <label className="flex flex-col text-xs font-semibold text-darkGrey">
              Status
              <select value={enabledFilter} onChange={(e) => setEnabledFilter(e.target.value as 'all' | 'enabled' | 'disabled')}
                aria-label="Filter by status" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
                <option value="all">All</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
          </div>

          <div className="overflow-x-auto rounded border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-bg text-left text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
                <tr>
                  <SortHeader label="Account" columnKey="account" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Department" columnKey="department" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Last sign-in" columnKey="lastSignIn" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortHeader label="Suggested" columnKey="suggested" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <th className="px-3 py-2 text-right"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white">
                {sorted.map((a) => (
                  <tr key={a.EntraAccountStagingId}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-navy">{a.DisplayName}</div>
                      <div className="text-xs text-darkGrey/60">{a.Upn ?? a.Mail ?? '—'}</div>
                    </td>
                    <td className="px-3 py-2">
                      {a.AccountEnabled
                        ? <span className="text-green">Enabled</span>
                        : <span className="inline-flex items-center gap-1 text-red"><ShieldAlert className="h-3.5 w-3.5" aria-hidden /> Disabled</span>}
                      {a.TriageStatus === 'NeedsVerify' && <span className="ml-1 rounded-full bg-orange/15 px-1.5 py-0.5 text-[10px] font-semibold text-orange">Verify</span>}
                    </td>
                    <td className="px-3 py-2 text-darkGrey/80">{a.Department ?? '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-darkGrey/80">{a.LastSignInDateTime ? formatDateTime(a.LastSignInDateTime) : '—'}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{a.SuggestedAccountTypeCode ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {a.TriageStatus !== 'Added' && (
                        <div className="flex items-center justify-end gap-3">
                          <button type="button" onClick={() => { setError(null); setAddTarget(a); }} className="text-xs font-semibold text-navy hover:underline">Add…</button>
                          {a.TriageStatus === 'Ignored'
                            ? <button type="button" onClick={() => patchM.mutate({ id: a.EntraAccountStagingId, status: 'New' })} className="text-xs font-semibold text-darkGrey/70 hover:underline">Restore</button>
                            : <button type="button" onClick={() => patchM.mutate({ id: a.EntraAccountStagingId, status: 'Ignored' })} className="text-xs font-semibold text-red hover:underline">Ignore</button>}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">Nothing here.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {addTarget && (
        <AddDialog
          item={addTarget}
          onClose={() => setAddTarget(null)}
          onAdded={() => { setAddTarget(null); invalidate(); queryClient.invalidateQueries({ queryKey: ['people'] }); }}
          onError={(m) => setError(m)}
        />
      )}

      <AlertDialog
        open={bulkOpen}
        onOpenChange={(o) => { if (!o) setBulkOpen(false); }}
        title={`Add all ${bulkEligible} pending account(s)?`}
        description={`This one-time action creates a Person or system account for every enabled account that has a suggested Account Type, using that suggestion. Accounts without a suggestion, and disabled accounts (which need verifying), are left for you to add individually. This can't be bulk-undone.`}
        confirmLabel={bulkM.isPending ? 'Adding…' : 'Add all'}
        onConfirm={() => bulkM.mutate()}
      />
    </>
  );
}

function AddDialog({ item, onClose, onAdded, onError }: {
  item: AccountStagingItem;
  onClose: () => void;
  onAdded: () => void;
  onError: (message: string) => void;
}) {
  const typesQuery = useQuery({ queryKey: ['account-types', 'active'], queryFn: ({ signal }) => listAccountTypes(true, signal) });
  const types = typesQuery.data ?? [];
  const [code, setCode] = useState<string>(item.SuggestedAccountTypeCode ?? '');

  const mutation = useMutation({
    mutationFn: () => addAccountFromStaging(item.EntraAccountStagingId, code),
    onSuccess: onAdded,
    onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not add the account.'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={`Add ${item.DisplayName}`}>
      <div className="w-full max-w-md rounded-md border border-border bg-white p-4 shadow-xl">
        <h2 className="font-heading text-lg font-bold text-navy">Add {item.DisplayName}</h2>
        <p className="mt-1 text-xs text-darkGrey/70">{item.Upn ?? item.Mail ?? ''}</p>
        {!item.AccountEnabled && (
          <p className="mt-2 flex items-start gap-2 rounded border border-orange/30 bg-orange/5 px-2 py-1.5 text-xs text-orange">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            This account is <span className="font-semibold">disabled</span> in Entra — verify it should be tracked before adding.
          </p>
        )}
        <label className="mt-3 block text-xs font-semibold text-darkGrey">
          Account type
          <select value={code} onChange={(e) => setCode(e.target.value)} aria-label="Account type" className="mt-1 block w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">Choose a type…</option>
            {types.map((t) => <option key={t.Code} value={t.Code}>{t.Name} ({t.Kind === 'Person' ? 'person' : 'system'})</option>)}
          </select>
        </label>
        <p className="mt-1 text-[11px] text-darkGrey/60">
          A <span className="font-semibold">Person</span>-kind type adds it under People; a
          <span className="font-semibold"> System</span>-kind type adds it as a system account.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg">Cancel</button>
          <button type="button" disabled={!code || mutation.isPending} onClick={() => mutation.mutate()}
            className="rounded bg-orange px-4 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40">
            {mutation.isPending ? 'Adding…' : 'Add account'}
          </button>
        </div>
      </div>
    </div>
  );
}
